import { css } from 'aphrodite';
import * as React from 'react';
import { ISupportGitHubIssueLink, SupportGitHubIssueType } from '../../../models/Support/ISupportGitHub';
import type { ISupportThread } from '../../../models/Support/ISupportThread';
import { ISupportTicket } from '../../../models/Support/ISupportTicket';
import {
	useCreateGitHubIssueMutation,
	useEscalateGitHubIssueMutation,
	useLinkGitHubIssueLinkMutation,
	useSupportGitHubQuery,
} from '../../../queries/Support/useSupportGitHub';
import { useGenerateGitHubIssueMarkdownMutation } from '../../../queries/Support/useSupportTicketMutations';
import { useSupportThreadsQuery } from '../../../queries/Support/useSupportThreadsQuery';
import { getApiErrorMessage } from '../../../utils/Support/supportHelpers';
import { LoadingSpinner } from '../../../web/components/LoadingSpinner';
import { Button } from '../../../web/components-v2/Button';
import {
	Dialog,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogPopup,
	DialogTitle,
} from '../../../web/components-v2/Dialog';
import { Field, FieldDescription, FieldError, FieldLabel } from '../../../web/components-v2/Field';
import { Input } from '../../../web/components-v2/Input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../web/components-v2/Select';
import { TextArea } from '../../../web/components-v2/TextArea';
import { ConfirmModal } from '../../components/supportShared/ConfirmModal';
import { supportSharedStyles as shared } from '../../components/supportShared/styles/supportSharedStyles';
import { buildAdminAccountUrl, buildAdminSupportTicketUrl } from '../../utils';
import { GitHubIssueBodyEditor, type TicketImage } from './GitHubIssueEditor/GitHubIssueBodyEditor';
import { getIssueImageReferences } from './GitHubIssueEditor/issueMarkdown';
import { styles } from './styles';
import { getTicketContactLabel } from './supportTicketPresentation';

const GITHUB_ISSUE_REGEX = /^https:\/\/github\.com\/([^/]+\/[^/]+)\/issues\/[1-9]\d*$/;
const EMPTY_REPOSITORIES: readonly string[] = [];
const GITHUB_IMAGE_MIME_TYPES = new Set(['image/gif', 'image/jpeg', 'image/png', 'image/webp']);

const getTicketImagesNewestFirst = (threads: ISupportThread[] | undefined): TicketImage[] => {
	const newestFirst = [...(threads ?? [])].sort((a, b) => Date.parse(b.creationDate) - Date.parse(a.creationDate));
	const imagesById = new Map<string, TicketImage>();
	for (const thread of newestFirst) {
		for (const attachment of thread.attachments ?? []) {
			const isImage = GITHUB_IMAGE_MIME_TYPES.has(attachment.mimeType?.toLowerCase() ?? '');
			if (isImage && !imagesById.has(attachment.id)) {
				imagesById.set(attachment.id, { attachment, receivedAt: thread.creationDate });
			}
		}
	}
	return Array.from(imagesById.values());
};

const getTicketAttachmentIds = (threads: ISupportThread[] | undefined) =>
	new Set((threads ?? []).flatMap(thread => (thread.attachments ?? []).map(attachment => attachment.id)));

const includesRepository = (repositories: readonly string[], repository: string) =>
	repositories.some(value => value.toLowerCase() === repository.toLowerCase());

export const LinkGitHubIssueModal: React.FC<{
	ticketId: string;
	onClose: () => void;
}> = ({ ticketId, onClose }) => {
	const [url, setUrl] = React.useState('');
	const [error, setError] = React.useState('');
	const panelQuery = useSupportGitHubQuery(ticketId);
	const repositories = panelQuery.data?.repositories ?? EMPTY_REPOSITORIES;
	const linkMutation = useLinkGitHubIssueLinkMutation(ticketId);

	const handleLink = () => {
		const trimmed = url.trim();
		const match = GITHUB_ISSUE_REGEX.exec(trimmed);
		if (!match) {
			setError('URL must match https://github.com/{owner}/{repo}/issues/{number}');
			return;
		}
		if (!includesRepository(repositories, match[1])) {
			setError(`Repository '${match[1]}' is not attached to this mailbox.`);
			return;
		}
		setError('');
		linkMutation.mutate(trimmed, {
			onSuccess: () => onClose(),
			onError: err => setError(getApiErrorMessage(err, 'Failed to link GitHub issue.')),
		});
	};

	return (
		<Dialog
			open
			onOpenChange={open => {
				if (!open) onClose();
			}}
		>
			<DialogPopup>
				<DialogHeader>
					<DialogTitle>Link GitHub Issue</DialogTitle>
				</DialogHeader>
				<div className={css(shared.modalBody)}>
					<Field data-invalid={!!error}>
						<FieldLabel htmlFor='github-issue-url'>GitHub Issue URL</FieldLabel>
						<Input
							id='github-issue-url'
							styles={[shared.modalInput, error && shared.modalInputError]}
							type='text'
							placeholder={`https://github.com/${repositories[0] || 'owner/repository'}/issues/123`}
							value={url}
							onChange={e => {
								setUrl(e.target.value);
								if (error) setError('');
							}}
							onKeyDown={e => {
								if (e.key === 'Enter') handleLink();
							}}
							autoFocus
						/>
						<FieldDescription>
							Attached repositories: {repositories.length > 0 ? repositories.join(', ') : 'none'}
						</FieldDescription>
						<FieldError>{error}</FieldError>
					</Field>
				</div>
				<DialogFooter>
					<Button variant='outline' size='sm' onClick={onClose}>
						Cancel
					</Button>
					<Button variant='primary' size='sm' onClick={handleLink} disabled={linkMutation.isPending}>
						{linkMutation.isPending ? 'Linking...' : 'Link'}
					</Button>
				</DialogFooter>
			</DialogPopup>
		</Dialog>
	);
};

type CreateIssueStep = 'describe' | 'generating' | 'review';

const buildIssueContextLinks = (ticket: ISupportTicket, ticketId: string) => {
	const lines: string[] = [];
	if (ticket.accountId) {
		lines.push(`**Account:** [${ticket.accountName || ticket.accountId}](${buildAdminAccountUrl(ticket.accountId)})`);
	}
	lines.push(`**Support Ticket:** [#${ticket.number}](${buildAdminSupportTicketUrl(ticketId)})`);
	return lines.join('\n');
};

export const CreateGitHubIssueModal: React.FC<{
	ticketId: string;
	ticket: ISupportTicket;
	onClose: () => void;
}> = ({ ticketId, ticket, onClose }) => {
	const contextLinks = buildIssueContextLinks(ticket, ticketId);
	const panelQuery = useSupportGitHubQuery(ticketId);
	const repositories = panelQuery.data?.repositories ?? EMPTY_REPOSITORIES;
	const threadsQuery = useSupportThreadsQuery(ticketId, { refetchInterval: false });
	const ticketImages = getTicketImagesNewestFirst(threadsQuery.data?.values);
	const [step, setStep] = React.useState<CreateIssueStep>('describe');
	const [repository, setRepository] = React.useState(() => repositories[0] || '');
	const [investigationQuestion, setInvestigationQuestion] = React.useState('');
	const [issueType, setIssueType] = React.useState<SupportGitHubIssueType>('AccountMaintenance');
	const [title, setTitle] = React.useState('');
	const [body, setBody] = React.useState('');
	const [generateError, setGenerateError] = React.useState('');
	const [createError, setCreateError] = React.useState('');

	const generateMutation = useGenerateGitHubIssueMarkdownMutation(ticketId);
	const createMutation = useCreateGitHubIssueMutation(ticketId);

	React.useEffect(() => {
		if (!includesRepository(repositories, repository)) setRepository(repositories[0] || '');
	}, [repositories, repository]);

	const handleGenerate = () => {
		const question = investigationQuestion.trim();
		if (!question) return;
		setGenerateError('');
		setStep('generating');
		generateMutation.mutate(question, {
			onSuccess: data => {
				setTitle(data.title || `${getTicketContactLabel(ticket)}: ${ticket.subject}`);
				setBody(`${contextLinks}\n\n## Investigation request\n\n${question}\n\n${data.body}`.trim());
				setStep('review');
			},
			onError: error => {
				setGenerateError(getApiErrorMessage(error, 'Failed to generate the investigation details. Please try again.'));
				setStep('describe');
			},
		});
	};

	// Attachment references are checked against the ticket's files, which only the threads carry.
	const attachmentsReady = threadsQuery.isSuccess;

	const handleCreateIssue = () => {
		if (!title.trim() || !repository || !attachmentsReady) return;
		const normalizedBody = body.trim();
		const ticketAttachmentIds = getTicketAttachmentIds(threadsQuery.data?.values);
		const images = getIssueImageReferences(normalizedBody);
		if (images.some(image => !ticketAttachmentIds.has(image.attachmentId))) {
			setCreateError('The body references an attachment that is not on this ticket. Remove it and try again.');
			return;
		}
		setCreateError('');
		createMutation.mutate(
			{
				repository,
				title: title.trim(),
				...(normalizedBody ? { body: normalizedBody } : {}),
				issueType,
				...(images.length > 0 ? { images } : {}),
			},
			{
				onSuccess: () => onClose(),
				onError: error => setCreateError(getApiErrorMessage(error, 'Failed to create GitHub issue.')),
			}
		);
	};

	return (
		<Dialog
			open
			dismissOnOutsideClick={!createMutation.isPending}
			onOpenChange={open => {
				if (!open && !createMutation.isPending) onClose();
			}}
		>
			<DialogPopup
				showCloseButton={!createMutation.isPending}
				styles={[styles.ghIssueDialog, step === 'review' && styles.ghIssueDialogWide]}
			>
				<DialogHeader>
					<DialogTitle>Create &amp; Investigate</DialogTitle>
					<DialogDescription>
						The selected repository workflow will investigate and return a customer-ready AI draft to this ticket.
					</DialogDescription>
				</DialogHeader>
				<div className={css(shared.modalBody)}>
					{step === 'describe' && (
						<>
							<Field>
								<FieldLabel htmlFor='github-investigation-repository'>Repository</FieldLabel>
								<Select value={repository} onValueChange={setRepository}>
									<SelectTrigger id='github-investigation-repository'>
										<SelectValue>{repository || 'No repository configured'}</SelectValue>
									</SelectTrigger>
									<SelectContent>
										{repositories.map(value => (
											<SelectItem key={value.toLowerCase()} value={value}>
												{value}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
								<FieldDescription>Repositories are managed in this mailbox's settings.</FieldDescription>
							</Field>
							<Field>
								<FieldLabel htmlFor='github-investigation-issue-type'>Issue type</FieldLabel>
								<Select value={issueType} onValueChange={value => setIssueType(value as SupportGitHubIssueType)}>
									<SelectTrigger id='github-investigation-issue-type'>
										<SelectValue>{issueType === 'Bug' ? 'Bug' : 'Account maintenance'}</SelectValue>
									</SelectTrigger>
									<SelectContent>
										<SelectItem value='AccountMaintenance'>Account maintenance</SelectItem>
										<SelectItem value='Bug'>Bug</SelectItem>
									</SelectContent>
								</Select>
								<FieldDescription>Use Bug only when the request describes a product defect.</FieldDescription>
							</Field>
							<Field data-invalid={!!generateError}>
								<FieldLabel htmlFor='github-investigation-question'>
									What should the repository workflow investigate?
								</FieldLabel>
								<TextArea
									id='github-investigation-question'
									styles={[styles.ghTextarea]}
									placeholder='Ask a specific question and include any detail the conversation may not make clear.'
									value={investigationQuestion}
									onChange={e => {
										setInvestigationQuestion(e.target.value);
										if (generateError) setGenerateError('');
									}}
									autoFocus
								/>
								<FieldDescription>
									The issue will include the account, support ticket, relevant conversation detail, and this exact
									question. The AI places ticket screenshots that help, and you can edit the draft next.
								</FieldDescription>
								<FieldError>{generateError}</FieldError>
							</Field>
						</>
					)}

					{step === 'generating' && (
						<div className={css(shared.modalSpinnerRow)}>
							<LoadingSpinner type='small' />
							<span>Generating issue markdown...</span>
						</div>
					)}

					{step === 'review' && (
						<>
							<Field>
								<FieldLabel htmlFor='github-investigation-title'>Title</FieldLabel>
								<Input
									id='github-investigation-title'
									styles={[shared.modalInput, styles.ghIssueTitleInput]}
									type='text'
									value={title}
									onChange={e => setTitle(e.target.value)}
									disabled={createMutation.isPending}
								/>
							</Field>
							<Field>
								<FieldLabel>Body</FieldLabel>
								<GitHubIssueBodyEditor
									value={body}
									onChange={setBody}
									images={ticketImages}
									disabled={createMutation.isPending}
								/>
							</Field>
							{threadsQuery.isError && (
								<FieldError>
									The ticket's attachments could not be loaded. Close and reopen this dialog to try again.
								</FieldError>
							)}
							{createError && <FieldError>{createError}</FieldError>}
						</>
					)}
				</div>
				<DialogFooter>
					{step === 'describe' && (
						<>
							<Button variant='outline' size='sm' onClick={onClose}>
								Cancel
							</Button>
							<Button
								variant='primary'
								size='sm'
								onClick={handleGenerate}
								disabled={!repository || !investigationQuestion.trim()}
							>
								Generate Issue
							</Button>
						</>
					)}
					{step === 'review' && (
						<>
							<Button
								variant='outline'
								size='sm'
								onClick={() => setStep('describe')}
								disabled={createMutation.isPending}
							>
								Back
							</Button>
							<Button
								variant='primary'
								size='sm'
								onClick={handleCreateIssue}
								disabled={!repository || !title.trim() || !attachmentsReady || createMutation.isPending}
							>
								{createMutation.isPending ? 'Creating & Investigating...' : 'Create & Investigate'}
							</Button>
						</>
					)}
				</DialogFooter>
			</DialogPopup>
		</Dialog>
	);
};

const isConflictError = (error: unknown) =>
	!!error && typeof error === 'object' && (error as { systemCode?: number }).systemCode === 409;

export const GitHubEscalationConfirmModal: React.FC<{
	ticketId: string;
	issue: ISupportGitHubIssueLink;
	onClose: () => void;
}> = ({ ticketId, issue, onClose }) => {
	const [error, setError] = React.useState('');
	const mutation = useEscalateGitHubIssueMutation(ticketId, issue.id);

	const handleConfirm = () => {
		if (!issue.latestTriageRunId) {
			setError('The latest investigation result is unavailable. Refresh the panel and try again.');
			return;
		}

		setError('');
		mutation.mutate(
			{ triageRunId: issue.latestTriageRunId },
			{
				onSuccess: onClose,
				onError: mutationError =>
					setError(
						isConflictError(mutationError)
							? 'A newer investigation result is available. Refresh the panel before escalating.'
							: getApiErrorMessage(mutationError, 'Failed to request engineering escalation. Please try again.')
					),
			}
		);
	};

	return (
		<ConfirmModal
			title='Escalate to Engineering?'
			message='This applies the existing escalation label to the investigation issue. The repository workflow will validate the result and create the engineering issue.'
			confirmLabel={mutation.isPending ? 'Requesting...' : 'Yes, escalate'}
			onConfirm={handleConfirm}
			onCancel={onClose}
			busy={mutation.isPending}
			error={error}
		/>
	);
};
